import ArrowForwardIosIcon from '@icons/ArrowForwardIosIcon'
import CloseIcon from '@icons/CloseIcon'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import PlaceHolder from 'images/placeholder-img.svg'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SwiperClass from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

// Like the card's arrows, but beside the image (the gallery leaves room on both sides).
const arrowSx = {
  position: 'absolute',
  top: '50%',
  zIndex: 2,
  color: 'secondary.main',
  transform: 'translateY(-50%)',
  '&:hover': { bgcolor: 'transparent', color: '#69c0b2' },
  '& svg': { fontSize: 28 },
} as const

interface Props {
  open: boolean
  // Unique on the page, for the gallery pagination element.
  id: string | number
  name: string
  // The card's slides (similar products included), starting at the card's current one.
  images: string[]
  activeIndex: number
  // A centered dialog instead of the bottom sheet (bigger screens).
  centered?: boolean

  onActiveIndexChange(index: number): void
  onClose(): void
}

// The quick add-to-cart form outside the card: in a bottom sheet (swipe down to close) on phones, in a centered
// dialog for narrow cards on bigger screens. The children mount when it opens, so the form still loads its data on
// click.
const ProductCardSheet: React.FC<Props> = ({
  open,
  id,
  name,
  images,
  activeIndex,
  centered = false,
  onActiveIndexChange,
  onClose,
  children,
}) => {
  const { t } = useTranslation()
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  // Kept to a phone-like width on bigger screens.
  const paperProps = { 'aria-label': name, sx: { width: '100%', maxWidth: 480, mx: 'auto' } }
  const content = (
    <>
      {!centered && <div className='product-card-sheet__handle' aria-hidden='true' />}
      <IconButton
        aria-label={t('close', 'Close')}
        onClick={onClose}
        // The theme forces IconButton's color to `inherit`, hence `color` in sx.
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'secondary.main', '& svg': { fontSize: 28 } }}
      >
        <CloseIcon viewBox='0 -960 960 960' />
      </IconButton>
      <div className='product-card-sheet__gallery'>
        <div className='product-card-sheet__slider'>
          <Swiper
            slidesPerView={1}
            spaceBetween={8}
            loop={images.length > 1}
            initialSlide={activeIndex}
            onSwiper={setSwiper}
            onSlideChange={swiper => onActiveIndexChange(swiper.realIndex)}
            pagination={images.length > 1 ? { clickable: true, el: `#product-card-sheet-pagination-${id}` } : false}
          >
            {images.map(image => (
              <SwiperSlide key={image}>
                <img className='product-card-sheet__image' src={image || PlaceHolder} alt={name} />
              </SwiperSlide>
            ))}
          </Swiper>
          {images.length > 1 && (
            <>
              <IconButton
                aria-label={t('button.previousImage', 'Previous image')}
                onClick={() => swiper?.slidePrev()}
                sx={{ ...arrowSx, left: -44 }}
              >
                <ArrowForwardIosIcon viewBox='0 -960 960 960' sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
              <IconButton
                aria-label={t('button.nextImage', 'Next image')}
                onClick={() => swiper?.slideNext()}
                sx={{ ...arrowSx, right: -44 }}
              >
                <ArrowForwardIosIcon viewBox='0 -960 960 960' />
              </IconButton>
            </>
          )}
        </div>
        <div id={`product-card-sheet-pagination-${id}`} className='swiper-pagination' />
      </div>
      <h3 className='product-card-sheet__title'>{name}</h3>
      {children}
    </>
  )

  if (centered) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ ...paperProps, className: 'product-card-sheet product-card-sheet--centered' }}
      >
        {content}
      </Dialog>
    )
  }
  return (
    <SwipeableDrawer
      anchor='bottom'
      open={open}
      onOpen={() => undefined}
      onClose={onClose}
      disableSwipeToOpen
      // SwipeableDrawer keeps its content mounted by default; every card has a sheet, and the form must only
      // mount (and query) once opened.
      ModalProps={{ keepMounted: false }}
      PaperProps={{ ...paperProps, className: 'product-card-sheet' }}
    >
      {content}
    </SwipeableDrawer>
  )
}

export default ProductCardSheet
